<template>
  <div class="booking-dialog" :class="{ 'is-overlay': isOverlay }">
    <v-card class="booking-dialog-card flex-column" tile :loading="isLoading ? 'secondary' : false">
      <v-toolbar
        style="height: auto;"
        elevation="0"
        class="py-3 toolbar-with-subheader flex-grow-0"
        color="white"
      >
        <div class="toolbar-titles-container overflow-hidden">
          <template v-if="isLoading">
            <v-toolbar-title class="pl-5 mt-1">
              <v-skeleton-loader
                type="heading"
                style="width: 200px; max-width: 200px;"
              ></v-skeleton-loader>
            </v-toolbar-title>
            <v-toolbar-title class="toolbar-subheader pl-5 mt-2">
              <v-skeleton-loader
                type="text"
                style="width: 120px; max-width: 120px;"
              ></v-skeleton-loader>
            </v-toolbar-title>
          </template>
          <template v-else>
            <v-toolbar-title class="pl-5 d-flex">
              {{ title }}
              <copy-to-clipboard-button
                :text="booking?.booking_number"
                class="ml-2"
              ></copy-to-clipboard-button>

              <v-chip
                v-if="!!booking?.booking_group"
                @click="openBookingGroup"
                class="ml-6 my-auto"
                color="pink"
                outlined
                small
              >
                <v-avatar left>
                  <v-icon small>mdi-calendar-multiple</v-icon>
                </v-avatar>
                Gruppbokning {{ booking.booking_group.name ?? booking.booking_group.booking_number }}
              </v-chip>
              <template v-if="booking && booking.invoicing_schedule && !booking.invoicing_schedule.is_completed">
                <recurring-invoicing-status
                  :invoicing-schedule="booking.invoicing_schedule"
                  class="ml-6 my-auto"
                ></recurring-invoicing-status>
              </template>
            </v-toolbar-title>
            <v-toolbar-title class="pl-5 toolbar-subheader">{{ subtitle }}</v-toolbar-title>
          </template>
        </div>
        <v-spacer></v-spacer>
        <simple-tooltip tooltip="Öppna i bokningskalendern">
          <v-btn
            icon
            :disabled="isLoading"
            @click="openBookingInCalendar"
            class="mr-2"
          >
            <v-icon>mdi-calendar-search</v-icon>
          </v-btn>
        </simple-tooltip>
        <simple-tooltip tooltip="Redigera bokning">
          <v-btn
            icon
            :disabled="isLoading"
            @click="editBooking"
            class="mr-2"
          >
            <v-icon>mdi-pen</v-icon>
          </v-btn>
        </simple-tooltip>
        <v-menu offset-x>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              class="ml-auto my-auto"
            >
              <v-icon>
                mdi-dots-vertical
              </v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-list>
              <v-list-item>
                <v-btn
                  @click="printCheckinConfirmation"
                  :disabled="!hasPrinter || !isCheckedIn"
                  :loading="isPrintingCheckinConfirmation"
                  block
                  text
                  color="primary"
                >
                  Skriv ut incheckningsbekräftelse
                </v-btn>
              </v-list-item>
              <v-list-item>
                <booking-addons-link-button
                  :addons-app-url="booking?.addons_app_url"
                ></booking-addons-link-button>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
        <v-btn icon v-if="isMobile" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-tabs
        v-model="tabIndex"
        color="secondary"
        show-arrows
        grow
      >
        <v-tabs-slider color="secondary"></v-tabs-slider>
        <v-tab href="#booking-information">
          <v-icon v-if="isMobile">mdi-book-marker-outline</v-icon>
          <span v-else>Bokningsinformation</span>
        </v-tab>
        <v-tab href="#booking-history">
          <v-icon v-if="isMobile">mdi-book-clock-outline</v-icon>
          <span v-else>Historik</span>
        </v-tab>
        <v-tab href="#charges">
          <v-icon v-if="isMobile">mdi-cash</v-icon>
          <span v-else>Avgifter</span>
        </v-tab>
        <v-tab href="#invoices-receipts">
          <v-badge
            :value="receiptsAndInvoicesCount !== ''"
            color="secondary"
            :content="receiptsAndInvoicesCount"
            offset-x="-2"
            offset-y="8"
          >
            <v-icon v-if="isMobile">mdi-receipt-text</v-icon>
            <span v-else>Kvitton och fakturor</span>
          </v-badge>
        </v-tab>
        <v-tab href="#emails">
          <v-badge
            :value="emailsCount !== ''"
            color="secondary"
            :content="emailsCount"
            offset-x="-2"
            offset-y="8"
          >
            <v-icon v-if="isMobile">mdi-email</v-icon>
            <span v-else>Email</span>
          </v-badge>
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-slide-y-transition leave-absolute>
        <v-tabs
          v-show="tabIndex === 'charges'"
          v-model="subTabIndexCharges"
          centered
          slider-color="secondary"
        >
          <v-tab
            href="#accommodation-charges"
            class="px-8"
          >
            Platsavgifter
          </v-tab>
          <v-tab
            href="#other-charges"
            class="px-8"
          >
            <v-badge
              :value="chargesCount !== ''"
              color="secondary"
              :content="chargesCount"
              offset-x="-2"
              offset-y="8"
            >
              Andra avgifter
            </v-badge>
          </v-tab>
        </v-tabs>
      </v-slide-y-transition>

      <v-slide-y-transition leave-absolute>
        <v-tabs
          v-show="tabIndex === 'invoices-receipts'"
          v-model="subTabIndexInvoicesReceipts"
          centered
          slider-color="secondary"
        >
          <v-tab
            href="#receipts"
            class="px-8"
          >
            <v-badge
              :value="receiptCount !== ''"
              color="secondary"
              :content="receiptCount"
              offset-x="-2"
              offset-y="8"
            >
              Kvitton
            </v-badge>
          </v-tab>
          <v-tab
            href="#invoices"
            class="px-8"
          >
            <v-badge
              :value="invoiceCount !== ''"
              color="secondary"
              :content="invoiceCount"
              offset-x="-2"
              offset-y="8"
            >
              Fakturor
            </v-badge>
          </v-tab>
          <v-tab
            href="#payments"
            class="px-8"
          >
            <v-badge
              :value="paymentsCount !== ''"
              color="secondary"
              :content="paymentsCount"
              offset-x="-2"
              offset-y="8"
            >
              Betalningar
            </v-badge>
          </v-tab>
        </v-tabs>
      </v-slide-y-transition>

      <v-divider v-show="tabIndex === 'invoices-receipts' || tabIndex === 'charges'"></v-divider>

      <v-tabs-items v-model="tabIndex">
        <v-tab-item value="booking-information">
          <v-sheet class="overflow-y-auto pa-3 full-height">
            <v-row class="ma-0 full-height">
              <v-col class="pb-0 full-height">
                <booking-data-list
                  :is-loading="isLoading"
                  :booking="booking"
                  inside-dialog
                  :is-open="isOpen"
                  :hidden-sections="['channel', 'booking']"
                  position-clickable
                  :customer-clickable="!isOverlay"
                  allow-reverse-checkin
                  allow-reverse-checkout
                  @close="close"
                ></booking-data-list>
                <v-list v-if="booking?.booking_fields?.length" class="pa-0 mb-4">
                  <v-list-item v-for="field in booking.booking_fields" :key="field.field_id">
                    <v-list-item-icon>
                      <v-icon>{{ bookingFieldIcon(field) }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-wrap">
                        {{ field.value === false ? 'Nej' : field.value === true ? 'Ja' : field.value }} {{ field.unit }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-wrap">{{ field.name }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
              <v-col class="pb-0 full-height">
                <template v-if="hasTally">
                  <tally-order
                    dense-header
                    :booking="booking"
                    :is-open="isOpen"
                    :is-loading="fetchingChunks.includes('booking')"
                  ></tally-order>
                </template>
                <template v-else-if="hasCamptrac">
                  <camptrac-order
                    dense-header
                    :booking="booking"
                    :is-open="isOpen"
                    :is-loading="fetchingChunks.includes('booking')"
                  ></camptrac-order>
                </template>

                <booking-input-remarks
                  class="pt-4"
                  :is-open="isOpen"
                  soft-border
                  :is-skeleton-loader="isLoading"
                  :booking-remarks="booking?.remarks"
                  :booking-id="booking?.id"
                  @remarks-updated="remarksUpdated"
                ></booking-input-remarks>
                <booking-requests
                  v-if="!!booking?.requests"
                  class="mt-4"
                  soft-border
                  :booking-requests="booking?.requests"
                  :is-skeleton-loader="isLoading"
                ></booking-requests>
              </v-col>
            </v-row>
          </v-sheet>
        </v-tab-item>
        <v-tab-item value="booking-history">
          <v-sheet class="overflow-y-auto pa-3 full-height">
            <editing-record-timeline
              :editing-records="editingRecords"
              :loading="fetchingChunks.includes('editing-records')"
            ></editing-record-timeline>
          </v-sheet>
        </v-tab-item>
        <v-tab-item value="charges">
          <v-tabs-items v-model="subTabIndexCharges">
            <v-tab-item value="accommodation-charges">
              <v-sheet class="overflow-y-auto pa-3 full-height">
                <template v-if="isSeasonBooking">
                  <accommodation-price-card
                    :booking="booking"
                    :loading="fetchingChunks.includes('booking')"
                  ></accommodation-price-card>
                </template>
                <template v-else>
                  <v-row class="full-height">
                    <v-col class="pb-0 full-height">
                      <accommodation-charges-calendar
                        :booking="booking"
                        :calendar-items="accommodationChargesCalendarItems"
                        :accommodation-sums="accommodationSums"
                        :loading="fetchingChunks.includes('accommodation-charges-calendar-items') || fetchingChunks.includes('accommodation-sums')"
                        :is-open="isOpen"
                        :single-column="isMobile"
                      ></accommodation-charges-calendar>
                    </v-col>
                  </v-row>
                </template>
              </v-sheet>
            </v-tab-item>
            <v-tab-item value="other-charges">
              <v-sheet class="overflow-y-auto pa-3 full-height">
                <charges-table
                  :charges="charges"
                  :booking="booking"
                  :show-total-row="charges?.length > 1"
                  :summary-toggle-enabled="showProductSummaryToggleOnChargesTable"
                  :loading="fetchingChunks.includes('charges')"
                ></charges-table>
              </v-sheet>
            </v-tab-item>
          </v-tabs-items>
        </v-tab-item>
        <v-tab-item value="invoices-receipts">
          <v-tabs-items v-model="subTabIndexInvoicesReceipts">
            <v-tab-item value="receipts">
              <v-sheet class="full-height">
                <receipt-list
                  class="full-height"
                  :receipts="receipts"
                  :show-total-row="receipts.length > 1"
                  flat
                  :loading="fetchingChunks.includes('receipts')"
                  :displayed-columns="['receipt_number', 'total', 'created_at']"
                ></receipt-list>
              </v-sheet>
            </v-tab-item>
            <v-tab-item value="invoices">
              <v-sheet class="full-height">
                <invoice-list
                  class="full-height"
                  :invoices="invoices"
                  :show-total-row="invoices.length > 1"
                  flat
                  :loading="fetchingChunks.includes('invoices')"
                  :displayed-columns="['invoice_number', 'total', 'remaining_amount', 'invoice_date', 'due_date', 'status']"
                ></invoice-list>
              </v-sheet>
            </v-tab-item>
            <v-tab-item value="payments">
              <v-sheet class="full-height">
                <payments-list
                  class="full-height"
                  :receipt-payments="receiptPayments"
                  :invoice-payment-applications="invoicePaymentApplications"
                  :show-total-row="(receiptPayments.length + invoicePaymentApplications.length) > 1"
                  flat
                  :loading="fetchingChunks.includes('payments')"
                  :displayed-columns="['paid_at', 'amount', 'payment_method', 'document']"
                ></payments-list>
              </v-sheet>
            </v-tab-item>
          </v-tabs-items>
        </v-tab-item>
        <v-tab-item value="emails">
          <v-sheet
            class="overflow-y-auto pa-3 full-height"
          >
            <email-timeline
              :emails="emails"
              :booking-id="booking?.id"
              :loading="fetchingChunks.includes('emails')"
              no-emails-message="Inga bekräftelser har skickats på den här bokningen"
            >
              <template v-slot:timeline-actions>
                <v-timeline-item
                  fill-dot
                  color="success"
                >
                  <template v-slot:icon>
                    <v-icon color="white">mdi-email-fast</v-icon>
                  </template>
                  <div class="d-inline-flex flex-column ga-2">
                    <v-btn
                      @click="sendBookingConfirmation"
                      :loading="isSendingBookingConfirmation"
                      :disabled="disableSendingBookingConfirmation"
                      color="success"
                      :class="{ 'btn-two-rows': isMobile }"
                    >
                      Skicka ny bokningsbekräftelse
                    </v-btn>
                    <template v-if="isCheckedIn || isCheckedOut">
                      <v-btn
                        @click="sendCheckinConfirmation"
                        :loading="isSendingCheckinConfirmation"
                        :disabled="isCheckedOut"
                        color="success"
                        :class="{ 'btn-two-rows': isMobile }"
                      >
                        Skicka incheckningsbekräftelse
                      </v-btn>
                    </template>
                    <template v-else>
                      <v-btn
                        @click="sendCheckinEmail"
                        :loading="isSendingCheckinEmail"
                        :disabled="disableSendingCheckinEmail"
                        color="success"
                        :class="{ 'btn-two-rows': isMobile }"
                      >
                        Skicka incheckningsmail
                      </v-btn>
                    </template>
                    <booking-addons-email-button
                      v-if="booking"
                      :booking-id="booking.id"
                      :disabled="disableSendingAddonsEmail"
                      @sent="markAwaitingAddonsEmail"
                    ></booking-addons-email-button>
                  </div>
                </v-timeline-item>
              </template>
            </email-timeline>
          </v-sheet>
        </v-tab-item>
      </v-tabs-items>
      <v-card-actions class="booking-dialog-footer full-width">
        <v-btn
          v-if="isCheckedOut"
          color="error"
          class="ma-2 px-4"
          large
          text
          :disabled="isLoading"
          :loading="isReversingCheckout"
          @click="reverseCheckout"
        >
          Ångra utcheckning
        </v-btn>
        <v-btn
          v-else-if="isCheckedIn"
          color="error"
          class="ma-2 px-4"
          large
          text
          :disabled="isLoading"
          :loading="isReversingCheckin"
          @click="reverseCheckin"
        >
          Ångra incheckning
        </v-btn>
        <template v-else-if="isDeleted">
          <v-tooltip
            :value="showCancellationStatusText"
            top
            content-class="error tooltip-top"
            nudge-bottom
            :open-on-click="false"
            :open-on-focus="false"
            :open-on-hover="false"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                color="secondary"
                class="ma-2 px-4"
                large
                text
                :disabled="isLoading"
                @click="openBookingCancellation"
              >
                Visa avbokning
              </v-btn>
            </template>
            <div>
              <div @click="cancellationTooltipClosed = true">{{ booking.cancellation_status }}</div>
            </div>
          </v-tooltip>
        </template>
        <v-btn
          v-else
          color="error"
          class="ma-2 px-4"
          large
          text
          :disabled="isLoading"
          :loading="isCancellingBooking"
          @click="cancelBooking"
        >
          Avboka
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn
          v-if="hasNotCheckedIn && !isDeleted"
          color="success"
          class="ma-2 px-4"
          large
          :disabled="isLoading"
          @click="checkInBooking"
        >
          Checka in
        </v-btn>
        <v-btn
          v-if="isCheckedIn"
          color="success"
          class="ma-2 px-4"
          large
          :text="!shouldCheckOutToday"
          :disabled="isLoading"
          @click="checkOutBooking"
        >
          Påbörja utcheckning
        </v-btn>

        <v-btn
          color="secondary"
          class="ma-2 ml-auto px-4"
          large
          :disabled="isLoading"
          @click="openBookingInRegister"
        >
          Öppna i kassan
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import BookingService from '@/services/BookingService'

import AccommodationPriceCard from './AccommodationPriceCard.vue'
import BookingAddonsEmailButton from './BookingAddonsEmailButton.vue'
import BookingAddonsLinkButton from './BookingAddonsLinkButton.vue'
import BookingDataList from './DataList.vue'
import BookingInputRemarks from './InputRemarks.vue'
import BookingRequests from './BookingRequests.vue'
import CopyToClipboardButton from '@/components/common/CopyToClipboardButton.vue'
import EditingRecordTimeline from '@/components/common/EditingRecordTimeline.vue'
import ChargesTable from '@/components/product/ChargesTable.vue'
import InvoiceList from '@/components/invoice/List.vue'
import PaymentsList from './PaymentsList.vue'
import ReceiptList from '@/components/receipt/List.vue'
import EmailTimeline from '@/components/email/Timeline.vue'
import TallyOrder from '@/components/tally/Order.vue'
import AccommodationChargesCalendar from './AccommodationChargesCalendar.vue'
import RecurringInvoicingStatus from '@/components/invoice/RecurringInvoicingStatus.vue'
import SimpleTooltip from '@/components/common/SimpleTooltip.vue'
import CamptracOrder from '@/components/camptrac/Order.vue'

export default {
  name: 'BookingDialogContent',
  components: {
    AccommodationPriceCard,
    BookingAddonsEmailButton,
    BookingAddonsLinkButton,
    BookingDataList,
    BookingInputRemarks,
    BookingRequests,
    CopyToClipboardButton,
    EditingRecordTimeline,
    ChargesTable,
    InvoiceList,
    PaymentsList,
    ReceiptList,
    EmailTimeline,
    TallyOrder,
    AccommodationChargesCalendar,
    RecurringInvoicingStatus,
    SimpleTooltip,
    CamptracOrder
  },
  props: {
    booking: Object,
    charges: Array,
    invoices: Array,
    receipts: Array,
    receiptPayments: Array,
    invoicePaymentApplications: Array,
    emails: Array,
    editingRecords: Array,
    accommodationChargesCalendarItems: Array,
    accommodationSums: Object,
    isLoading: Boolean,
    isOverlay: Boolean,
    isOpen: Boolean
  },
  data: () => ({
    cancellationTooltipClosed: false,
    isPrintingCheckinConfirmation: false
  }),
  computed: {
    ...mapState(
      {
        fetchingChunks: state => state.booking.dialog.fetchingChunks,
        openDialogTab: state => state.booking.dialog.openTab,
        openOverlayTab: state => state.customer.dialog.bookingOverlay.openTab,
        isCancellingBookingIds: state => state.booking.isCancellingBookingIds,
        isSendingConfirmationBookingIds: state => state.booking.isSendingConfirmationBookingIds,
        isSendingCheckinEmailBookingIds: state => state.booking.isSendingCheckinEmailBookingIds,
        isSendingCheckinConfirmationBookingIds: state => state.booking.isSendingCheckinConfirmationBookingIds,
        isReversingCheckinBookingIds: state => state.booking.isReversingCheckinBookingIds,
        isReversingCheckoutBookingIds: state => state.booking.isReversingCheckoutBookingIds,
        triggerInvoiceUpdated: state => state.invoice.triggerInvoiceUpdated,
        triggerBookingUpdated: state => state.booking.triggerBookingUpdated,
        triggerAccommodationChargesUpdated: state => state.booking.triggerAccommodationChargesUpdated,
        triggerChargesUpdated: state => state.booking.triggerChargesUpdated,
        isElectron: state => state.isElectron,
        defaultPrinter: state => state.workstation?.default_printer,
        accessDriver: state => state.workstation?.access_driver
      }
    ),
    openTab: function () {
      if (this.isOverlay) {
        return this.openOverlayTab
      }
      return this.openDialogTab
    },
    tabIndex: {
      get () {
        return this.openTab.tabIndex
      },
      set (val) {
        if (this.isOverlay) {
          this.$store.commit('customer/SET_BOOKING_OVERLAY_OPEN_TAB', { tabIndex: val })
        } else {
          this.$store.commit('booking/SET_DIALOG_OPEN_TAB', { tabIndex: val })
        }
      }
    },
    subTabIndexCharges: {
      get: function () {
        if (this.subTabIndex !== 'accommodation-charges' && this.subTabIndex !== 'other-charges') {
          return 'accommodation-charges'
        }
        return this.subTabIndex
      },
      set: function (val) {
        if (this.tabIndex === 'charges') {
          this.subTabIndex = val
        }
      }
    },
    subTabIndexInvoicesReceipts: {
      get: function () {
        if (this.subTabIndex !== 'receipts' && this.subTabIndex !== 'invoices' && this.subTabIndex !== 'payments') {
          return this.defaultSubTab
        }
        return this.subTabIndex
      },
      set: function (val) {
        if (this.tabIndex === 'invoices-receipts') {
          this.subTabIndex = val
        }
      }
    },
    subTabIndex: {
      get () {
        return this.openTab.subTabIndex
      },
      set (val) {
        if (this.isOverlay) {
          this.$store.commit('customer/SET_BOOKING_OVERLAY_OPEN_TAB', { subTabIndex: val })
        } else {
          this.$store.commit('booking/SET_DIALOG_OPEN_TAB', { subTabIndex: val })
        }
      }
    },
    defaultSubTab: function () {
      if (this.invoices.length === 0) {
        return 'receipts'
      }
      if (this.receipts.length === 0) {
        return 'invoices'
      }
      const mostRecentReceipt = this.receipts[0].created_at
      const mostRecentInvoice = this.invoices[0].created_at
      if (mostRecentInvoice.isAfter(mostRecentReceipt)) {
        return 'invoices'
      }
      return 'receipts'
    },
    hasTally: function () {
      return this.accessDriver === 'tally'
    },
    hasCamptrac: function () {
      return this.accessDriver === 'camptrac'
    },
    isCancellingBooking: function () {
      return this.booking && this.isCancellingBookingIds.includes(this.booking.id)
    },
    isReversingCheckin: function () {
      return this.booking && this.isReversingCheckinBookingIds.includes(this.booking.id)
    },
    isReversingCheckout: function () {
      return this.booking && this.isReversingCheckoutBookingIds.includes(this.booking.id)
    },
    isDeleted: function () {
      return this.booking && !!this.booking.deleted_at
    },
    disableSendingBookingConfirmation: function () {
      return this.isCancellingBooking || this.isDeleted || this.isCheckedOut
    },
    disableSendingCheckinEmail: function () {
      return !this.booking || this.isCancellingBooking || this.isCheckedIn || this.isDeleted || this.isCheckedOut || this.booking.check_in.isAfter(window.dayjs.today())
    },
    disableSendingAddonsEmail: function () {
      // Avbokade och utcheckade bokningar kan inte öppnas i tillvalsappen
      return !this.booking?.addons_app_url || this.isCancellingBooking
    },
    isSeasonBooking: function () {
      return this.booking && this.booking.type === 'season'
    },
    isSendingBookingConfirmation: function () {
      return this.booking && this.isSendingConfirmationBookingIds.includes(this.booking.id)
    },
    isSendingCheckinEmail: function () {
      return this.booking && this.isSendingCheckinEmailBookingIds.includes(this.booking.id)
    },
    isSendingCheckinConfirmation: function () {
      return this.booking && this.isSendingCheckinConfirmationBookingIds.includes(this.booking.id)
    },
    isCheckedIn: function () {
      if (!this.booking) {
        return false
      }
      return this.booking.checked_in_at !== null && this.booking.checked_out_at === null
    },
    isCheckedOut: function () {
      if (!this.booking) {
        return false
      }
      return this.booking.checked_out_at !== null
    },
    shouldCheckOutToday: function () {
      const today = window.dayjs.today()
      return this.isCheckedIn && this.booking.check_out.isSameOrBefore(today)
    },
    hasNotCheckedIn: function () {
      if (!this.booking) {
        return false
      }
      return this.booking.checked_in_at === null
    },
    chargesCount: function () {
      return this.badgeCount(this.charges.length)
    },
    receiptCount: function () {
      return this.badgeCount(this.receipts.length)
    },
    invoiceCount: function () {
      return this.badgeCount(this.invoices.length)
    },
    paymentsCount: function () {
      return this.badgeCount(this.receiptPayments.length + this.invoicePaymentApplications.length)
    },
    receiptsAndInvoicesCount: function () {
      return this.badgeCount((this.receipts?.length ?? 0) + (this.invoices?.length ?? 0))
    },
    emailsCount: function () {
      return this.badgeCount(this.emails.length)
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.smAndDown
    },
    title: function () {
      return this.booking && this.booking.title
    },
    subtitle: function () {
      return this.booking && this.booking.subtitle
    },
    showCancellationStatusText: function () {
      return ['Inväntar dokument', 'Oavslutad'].includes(this.booking?.cancellation_status) && !this.cancellationTooltipClosed && this.isOpen && !this.isMobile && this.fetchingChunks.length === 0
    },
    hasBookvisitInformation: function () {
      return !!this.booking?.bookvisit_information
    },
    showProductSummaryToggleOnChargesTable: function () {
      // Visa togglen för att visa produktbeskrivning i charges table om det finns minst två charges med samma product_id
      if (!this.charges) {
        return false
      }
      const productIdCounts = this.charges.reduce((counts, charge) => {
        if (charge.product_id) {
          counts[charge.product_id] = (counts[charge.product_id] || 0) + 1
        }
        return counts
      }, {})
      return Object.values(productIdCounts).some(count => count > 1)
    },
    hasPrinter: function () {
      return !!this.defaultPrinter
    }
  },
  methods: {
    bookingFieldIcon: function (field) {
      const presetIcons = {
        license_plate: 'mdi-car-info',
        vehicle_length_range: 'mdi-ruler',
        vehicle_length_exact: 'mdi-ruler',
        vehicle_width_range: 'mdi-arrow-expand-horizontal',
        vehicle_width_exact: 'mdi-arrow-expand-horizontal',
        vehicle_weight_range: 'mdi-weight-kilogram',
        tent_area_range: 'mdi-tent',
        tent_area_exact: 'mdi-tent'
      }
      return presetIcons[field.predefined_key] || 'mdi-form-textbox'
    },
    badgeCount: function (count) {
      if (!count) {
        return ''
      }
      return count > 99 ? '99+' : count
    },
    close: function () {
      this.$emit('close')
    },
    openBookingInCalendar: function () {
      this.$store.dispatch('calendar/requestBookingFocus', this.booking.id)
      this.$store.dispatch('calendar/highlightBookings', [this.booking.id])
      this.$store.dispatch('closeDialogs')
      this.$router.push({ name: 'Calendar' })
      this.close() // behövs för om detta är en booking overlay i en customer dialog så stängs den inte med closeDialogs, så den är öppen nästa gång customer dialog öppnas
    },
    openBookingInRegister: function () {
      this.$store.dispatch('closeDialogs')
      this.$router.push({
        name: 'PosRegister',
        params: {
          chargable: 'booking',
          id: this.booking.id
        }
      })
      this.close() // behövs för om detta är en booking overlay i en customer dialog så stängs den inte med closeDialogs, så den är öppen nästa gång customer dialog öppnas
    },
    checkOutBooking: function () {
      this.$store.dispatch('closeDialogs')
      this.$store.dispatch('booking/startCheckOut', { booking: this.booking })
      this.close() // behövs för om detta är en booking overlay i en customer dialog så stängs den inte med closeDialogs, så den är öppen nästa gång customer dialog öppnas
    },
    checkInBooking: function () {
      this.$store.dispatch('closeDialogs')
      this.$store.dispatch('booking/checkIn', { booking: this.booking })
      this.close() // behövs för om detta är en booking overlay i en customer dialog så stängs den inte med closeDialogs, så den är öppen nästa gång customer dialog öppnas
    },
    printCheckinConfirmation: function () {
      // Ska använda pinkod och CustomInformation så det skrivs ut från server
      // Knappen är disabled till tally provision skapats, så server ska inte behöva vänta in detta
      this.isPrintingCheckinConfirmation = true
      BookingService.printCheckinConfirmation({ bookingId: this.booking.id })
        .finally(() => {
          this.isPrintingCheckinConfirmation = false
        })
    },
    cancelBooking: function () {
      if (this.hasBookvisitInformation) {
        this.$store.dispatch('snackbars/createSnackbar', {
          color: 'warning',
          text: 'Externa bokningar måste avbokas via BookVisit'
        })
        return false
      }
      const bookingId = this.booking.id
      this.cancellationTooltipClosed = true // vill inte visa denna tooltip direkt vid avbokning
      this.$store.commit('SET_OPEN_CONFIRMATION_DIALOG', {
        header: `Avboka ${this.booking.title}`,
        body: 'Är du säker på att du vill avboka den här bokningen?',
        confirmationFunction: 'booking/cancel',
        confirmationArgument: bookingId
      })
    },
    reverseCheckin: function () {
      if (!this.isCheckedIn) {
        return false
      }
      this.$store.dispatch('booking/reverseCheckin', this.booking.id)
    },
    reverseCheckout: function () {
      if (!this.isCheckedOut) {
        return false
      }
      this.$store.dispatch('booking/reverseCheckout', this.booking.id)
    },
    openBookingCancellation: function () {
      this.cancellationTooltipClosed = true
      this.$store.dispatch('cancellationPolicy/openBookingCancellationDialog', { bookingId: this.booking.id })
    },
    sendBookingConfirmation: function () {
      this.$store.dispatch('booking/sendBookingConfirmation', { bookingId: this.booking.id })
    },
    sendCheckinEmail: function () {
      this.$store.dispatch('booking/sendCheckinEmail', { bookingId: this.booking.id })
    },
    sendCheckinConfirmation: function () {
      this.$store.dispatch('booking/sendCheckinConfirmation', { bookingId: this.booking.id })
    },
    markAwaitingAddonsEmail: function () {
      this.$store.commit('booking/SET_IS_AWAITING_EMAIL_SAVED_BOOKING_ID', { bookingId: this.booking.id, val: true })
    },
    remarksUpdated: function ({ bookingId, remarks }) {
      if (this.booking && this.booking.id === bookingId) {
        this.$store.commit('booking/SET_DIALOG_BOOKING_REMARKS', remarks)
      }
      if (this.booking && this.booking.id === bookingId && !this.fetchingChunks.includes('editing-records')) {
        this.$store.dispatch('booking/getBookingEditingRecords', this.booking.id)
      }
    },
    editBooking: function () {
      return this.$store.dispatch('bookingForm/startEditBooking', this.booking)
    },
    openBookingGroup: function () {
      if (!this.booking || !this.booking.booking_group) {
        return
      }
      this.$store.dispatch('bookingGroup/openDialogById', { bookingGroupId: this.booking.booking_group.id })
      this.close() // behövs för om detta är en booking overlay i en customer dialog så stängs den inte med closeDialogs, så den är öppen nästa gång customer dialog öppnas
    },
    openBookvisitReservation: function () {
      const bookingCode = this.booking?.bookvisit_booking_code
      if (bookingCode) {
        const url = `https://admin.bookvisit.com/Reservation/managereservation?BookingNumberOrCustomerName=${bookingCode}`
        if (this.isElectron) {
          window.shell.openExternalLink(url)
        } else {
          window.open(url, '_blank')
        }
      }
    }
  },
  watch: {
    triggerInvoiceUpdated: function () {
      // variabel som uppdateras efter fakturaändringar
      if (this.isOpen && this.booking && this.booking.id) {
        this.$store.dispatch('booking/getBookingInvoices', this.booking.id)
        this.$store.dispatch('booking/getBookingPayments', this.booking.id)
        this.$store.dispatch('booking/getBookingCharges', this.booking.id)
      }
    },
    triggerBookingUpdated: function () {
      // De flesta booking ändringar leder till nya editingRecords, hämtar om
      if (this.isOpen && this.booking && this.booking.id && !this.fetchingChunks.includes('editing-records')) {
        this.$store.dispatch('booking/getBookingEditingRecords', this.booking.id)
      }
    },
    triggerAccommodationChargesUpdated: function () {
      // Direka ändringar i accommodation charges triggar denna
      if (this.isOpen && this.booking && this.booking.id) {
        if (!this.fetchingChunks.includes('editing-records')) {
          this.$store.dispatch('booking/getBookingEditingRecords', this.booking.id)
        }
        if (!this.fetchingChunks.includes('accommodation-charges-calendar-items')) {
          this.$store.dispatch('booking/getBookingCalendarItems', this.booking.id)
        }
      }
    },
    triggerChargesUpdated: function () {
      if (this.isOpen && this.booking && this.booking.id) {
        this.$store.dispatch('booking/getBookingCharges', this.booking.id)
      }
    },
    isOpen: function () {
      if (!this.isOpen) {
        this.cancellationTooltipClosed = false
      }
    }
  }
}
</script>
